import { ICommonUtilInterface } from "../../domain/common/common-util.interface";
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { HttpService } from "../http/http";
import { ChannelRepository } from "../../domain/channel-repository";
import { UserService } from "../../domain/user-service";
import { Auth } from "../auth";
import { UserRepository } from "../../domain/user-repository";
import { IBroadcastsRepository } from "../../domain/broadcasts/i-broadcasts-repository";
import { BroadcastPreset } from "../../domain/broadcasts/broadcast-preset";
import { Switcher } from "../../domain/broadcasts/switcher";
import { SwitcherItem } from "../../domain/broadcasts/switcher-item";
import { ESwitcherAssetType } from "../../domain/broadcasts/switcher-item.type.enum";
import {
  broadcastBlockAssetItem,
  assetBlockItemExtendedData,
  broadcastAssetItemExtendedData,
} from "../../domain/broadcasts/asset-block-item-extended-data";
import { BroadcastPresetAudioChannel } from "../../domain/broadcasts/broadcast-preset-audio-channel";

@Injectable()
export class CachedBroadcastsRepository implements IBroadcastsRepository {
  private _cache: BroadcastPreset[] = [];
  public updated = new Subject<BroadcastPreset>();
  public created = new Subject<BroadcastPreset>();
  public deleted = new Subject<BroadcastPreset>();
  public blockTypeChanged = new Subject<SwitcherItem>();
  constructor(
    private _commonUtils: ICommonUtilInterface,
    private _httpService: HttpService,
    private _channelRepository: ChannelRepository,
    private _userService: UserService,
    private _auth: Auth,
    private _userRepository: UserRepository,
  ) {
    console.log("CachedBroadcastsRepository->constructor()");
  }

  async init(): Promise<void> {
    const response = await this._httpService.get(`Broadcasts`);
    this._cache.push(...this._toBroadcasts(response));
    /*for (let j = 0; j < 5; j++) {
      this._cache.push(BroadcastPreset.createNew('test' + j, this._commonUtils));
    }*/
  }

  async create(broadcastToCreate: BroadcastPreset): Promise<void> {
    try {
      await this._upsertBroadcastApiRequest(broadcastToCreate);

      this._cache.push(broadcastToCreate);
      this.created.next(broadcastToCreate);

      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  }
  async update(broadcastPreset: BroadcastPreset): Promise<void> {
    try {
      const existingBroadcast = (await this.get(null, broadcastPreset.id))[0];
      if (existingBroadcast) {
        await this._upsertBroadcastApiRequest(broadcastPreset);
        this._cache[this._cache.indexOf(existingBroadcast)] = broadcastPreset;
        this.updated.next(broadcastPreset);
      }
    } catch (e) {
      return Promise.reject(e);
    }

    return Promise.resolve();
  }

  async delete(broadcastId: string): Promise<void> {
    const existingBroadcast = (await this.get(null, broadcastId))[0];
    if (existingBroadcast) {
      await this._httpService.delete(`Broadcasts?Uid=${broadcastId}`);
      this._cache.splice(
        this._cache.indexOf(this._cache.find((l) => l.id === broadcastId)),
        1,
      );
      this.deleted.next(existingBroadcast);
    }

    return Promise.resolve();
    //return this._httpService.delete(`Layouts?Uid=${layoutId}`);
  }

  async get(
    nameFilter?: string,
    broadcastId?: string,
  ): Promise<BroadcastPreset[]> {
    let result = [];
    if (nameFilter && nameFilter.length > 0) {
      let text = nameFilter.toLowerCase();
      result = this._cache.filter((l) => l.name.toLowerCase().includes(text));
    } else {
      result = [...this._cache];
    }

    if (broadcastId && broadcastId.length > 0) {
      result = result.filter((l) => l.id === broadcastId);
    }

    return Promise.resolve(result);
  }

  private _upsertBroadcastApiRequest(
    broadcastPreset: BroadcastPreset,
  ): Promise<boolean> {
    //return Promise.resolve(true);
    return this._httpService.post(
      `Broadcasts/CreateOrUpdate`,
      this._toUpsertRequest(broadcastPreset),
    );
  }

  private _toBroadcasts(response: any): BroadcastPreset[] {
    return response.map((broadcastResponse) => {
      const audioChannels: BroadcastPresetAudioChannel[] = [];
      if (broadcastResponse.AudioChannels) {
        for (let i = 0; i < broadcastResponse.AudioChannels.length; i++) {
          const audioChannel = broadcastResponse.AudioChannels[i];
          audioChannels.push(
            new BroadcastPresetAudioChannel(
              audioChannel.AssetType,
              audioChannel.SourceUid,
              audioChannel.Name,
              audioChannel.Language,
              audioChannel.PID,
              audioChannel.VolumeLevel,
              true,
              audioChannel.IsMuted,
              audioChannel.IsClearedForAir,
              true, // previewListenMuted - synced from playlist on load
            ),
          );
        }
      }
      const switchers: Switcher[] = [];
      for (let i = 0; i < broadcastResponse.AssetItems.length; i++) {
        const assetItem = broadcastResponse.AssetItems[i];
        if (switchers.length != assetItem.switcher_index + 1) {
          const assetItems = [];
          for (let j = 0; j < 12; j++) {
            assetItems.push(
              SwitcherItem.createEmptyItem(this._commonUtils.generateGuid(), j),
            );
          }
          switchers.push(new Switcher(assetItem.switcher_name, assetItems));
        }

        const assetType = assetItem.asset_type as ESwitcherAssetType;
        let assetId;
        let data = {};
        switch (assetType) {
          case ESwitcherAssetType.Media:
            assetId = assetItem.asset_media_id;
            break;
          case ESwitcherAssetType.Gfx:
            assetId = assetItem.asset_gfx_id;
            break;
          case ESwitcherAssetType.Layout:
            assetId = assetItem.asset_layout_id;
            break;
          case ESwitcherAssetType.Block:
          case ESwitcherAssetType.Stack:
            assetId = assetItem.asset_block_id ?? assetItem.asset_stack_id;
            data = assetItem.asset_block_extended_data;
            if (data["extendedData"]) {
              data = new assetBlockItemExtendedData(
                data["assets"],
                data["extendedData"],
              );
            }
            break;
          case ESwitcherAssetType.LiveFeed:
            assetId = assetItem.asset_live_feed_uid;
            break;
        }
        switchers[assetItem.switcher_index].items[assetItem.asset_index] =
          new SwitcherItem(
            assetItem.asset_uid,
            assetType,
            assetItem.asset_index,
            assetItem.asset_name,
            assetItem.asset_preview_url,
            assetId,
            assetItem.asset_som_ms,
            assetItem.asset_duration_ms,
            false,
            assetItem.asset_is_program,
            assetItem.asset_is_preview,
            data,
            assetItem.col_span ?? 1,
            assetItem.row_span ?? 1,
          );
      }
      return new BroadcastPreset(
        broadcastResponse.Uid,
        broadcastResponse.Name,
        broadcastResponse.ThumbnailUrl,
        switchers,
        audioChannels,
      );
    });
  }

  private _toUpsertRequest(broadcastPreset: BroadcastPreset): any {
    return {
      Name: broadcastPreset.name,
      Uid: broadcastPreset.id,
      AudioChannels: broadcastPreset.audioChannels.map((c) => {
        return {
          AssetType: c.assetType,
          SourceUid: c.sourceUid,
          Name: c.name,
          Language: c.language,
          PID: c.pid,
          VolumeLevel: c.volumeLevel,
          IsMuted: c.isMuted,
          IsClearedForAir: c.isClearedForAir,
        };
      }),
      ThumbnailUrl: broadcastPreset.thumbnailUrl,
      SwitchersRequests: broadcastPreset.switchers.map((s, i) => {
        return {
          Name: s.name,
          Index: i,
          AssetItemRequests: s.items.map((item) => {
            return {
              Uid: item.uid,
              Type: item.type,
              Index: item.index,
              AssetName: item.assetName,
              IsProgram: item.isProgram,
              IsPreview: item.isPreview,
              AssetId: item.assetId,
              SomMs: item.somMs,
              DurationMs: item.durationMs,
              ColSpan: item.colSpan ?? 1,
              RowSpan: item.rowSpan ?? 1,
            };
          }),
        };
      }),
    };
  }
  async changeBlockType(
    switcherItem: SwitcherItem,
    broadcastUid: string,
  ): Promise<void> {
    var broadcast = (await this.get(null, broadcastUid))[0];
    if (broadcast) {
      broadcast.switchers.forEach((s) => {
        s.items.forEach(async (i) => {
          if (i.uid == switcherItem.uid) {
            i.type = switcherItem.type;
            if (!i.data) {
              i.data =
                switcherItem.data ??
                (await this.getBlockExtendedData(broadcastUid, switcherItem));
            }
          }
        });
      });

      await this.update(broadcast);
      this.blockTypeChanged.next(switcherItem);
    }
  }

  async getBlockExtendedData(
    broadcastUid: string,
    switcherItem: SwitcherItem,
  ): Promise<assetBlockItemExtendedData> {
    const response = await this._httpService.get(
      `Broadcasts/GetBlockExtendedData?broadcastUid=${broadcastUid}&BlockId=${switcherItem.assetId}`,
    );

    var assets = response.assets.map((item) => {
      return new broadcastBlockAssetItem(
        item.id,
        item.name,
        item.duration,
        item.thumbnail,
      );
    });
    var data = new assetBlockItemExtendedData(assets, response.extendedData);
    this._cache
      .find((b) => b.id === broadcastUid)
      ?.switchers.forEach((s) => {
        s.items.forEach((i) => {
          if (i.uid == switcherItem.uid) {
            i.data = data;
          }
        });
      });
    return data;
  }
  async updateBlockExtendedData(
    broadcastUid: string,
    assetId: string | number,
    assetUid: string,
    data: assetBlockItemExtendedData,
    pushUpdate: boolean = true,
  ): Promise<void> {
    const serverData = { broadcastAssetItems: data.broadcastAssetItems };
    await this._httpService.post(`Broadcasts/UpdateBlockExtendedData`, {
      broadcastUid: broadcastUid,
      blockId: assetId,
      extendedData: serverData,
    });
    const broadcast = this._cache.find((b) => b.id === broadcastUid);
    if (broadcast) {
      broadcast.switchers.forEach((s) => {
        s.items.forEach((i) => {
          if (
            (i.type === ESwitcherAssetType.Block ||
              i.type === ESwitcherAssetType.Stack) &&
            i.data &&
            i.assetId === assetId
          ) {
            (i.data as assetBlockItemExtendedData).broadcastAssetItems[
              assetUid
            ] = data.getBroadcastAssetItem(
              assetUid,
            ) as broadcastAssetItemExtendedData;
          }
        });
      });
      //pushUpdate && (await this.update(broadcast));
      return Promise.resolve();
    }
  }
  async refreshBroadcastPreset(broadcastPresetUid: string): Promise<void> {
    const response = await this._httpService.get(
      `Broadcasts?Uids=${broadcastPresetUid}`,
    );
    const broadcastPreset = this._toBroadcasts(response)[0];
    this._cache[this._cache.indexOf(broadcastPreset)] = broadcastPreset;
    this.updated.next(broadcastPreset);
  }
}
